import * as React from 'react';
import { useSelector } from 'react-redux';
import { skontodefinitionenSelector } from 'store/lookups/selector';
import { requestSkontodefinitionen } from 'store/lookups/actions';
import {
    addSkontodefinition,
    defaultSkontodefinition,
    Skontodefinition,
    updateSkontodefinition,
} from 'api/skontodefinition';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { DefaultButton, Dialog, DialogFooter, DialogType, Fabric, PrimaryButton } from '@fluentui/react';
import { viewStyles } from 'styles/viewStyles';
import { formStyles } from 'styles/formStyles';
import { FormTextField } from 'formComponents/FormTextField';
import { SavingState } from 'api/savingState';
import { CheckboxCompositeIcon, CheckboxIcon } from '@fluentui/react-icons';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        height: 'calc(100vh - 120px)',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
    },
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

export const Skontodefinitionen: React.FC = () => {
    const skontodefinitionen = useSelector(skontodefinitionenSelector);

    const [editData, setEditData] = React.useState<Skontodefinition | null>(null);

    const save = async () => {
        if (!editData) {
            return;
        }
        if (editData?.art === -1) {
            await addSkontodefinition(editData);
        } else {
            await updateSkontodefinition(editData);
        }
        await requestSkontodefinitionen();
        setEditData(null);
    };

    return (
        <Fabric className={styles.container}>
            <div className={styles.searchBar}>
                <div />

                <PrimaryButton
                    onClick={() => setEditData({ ...defaultSkontodefinition, art: -1 })}
                    text="Neue Skontodefinition"
                />
            </div>
            <div className={viewStyles.listContainerScroll}>
                <table className={formStyles.lookupTable}>
                    <thead className={formStyles.lookupTableHead}>
                        <tr>
                            <td className={formStyles.lookupTableCell}>Text</td>
                            <td className={formStyles.lookupTableCell}>Zahlfrist</td>
                            <td className={formStyles.lookupTableCell}>Skonto-Prozent</td>
                            <td className={formStyles.lookupTableCell}>Skonto-Frist</td>
                            <td className={formStyles.lookupTableCell}>Archiv</td>
                            <td className={formStyles.lookupTableCell} />
                        </tr>
                    </thead>
                    <tbody className={formStyles.lookupTableBody}>
                        {skontodefinitionen
                            .sort((a, b) => a.text.localeCompare(b.text))
                            .map((skontodefinition) => (
                                <tr key={skontodefinition.art} className={formStyles.looupTableRow}>
                                    <td className={formStyles.lookupTableCell}>{skontodefinition.text}</td>
                                    <td className={formStyles.lookupTableCell}>{skontodefinition.zahlfrist}</td>
                                    <td className={formStyles.lookupTableCell}>{skontodefinition.skP1}</td>
                                    <td className={formStyles.lookupTableCell}>{skontodefinition.skF1}</td>
                                    <td className={formStyles.lookupTableCell}>
                                        {skontodefinition.archiv ? (
                                            <CheckboxCompositeIcon className={formStyles.checkbox} />
                                        ) : (
                                            <CheckboxIcon className={formStyles.checkbox} />
                                        )}
                                    </td>
                                    <td className={formStyles.lookupTableActionCell}>
                                        <DefaultButton
                                            className={formStyles.lookupTableEditButton}
                                            onClick={() => setEditData({ ...skontodefinition })}
                                            text="Bearbeiten"
                                        />
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            {editData && (
                <Dialog
                    hidden={false}
                    minWidth={800}
                    dialogContentProps={{
                        title: 'Skontodefinition bearbeiten',
                        type: DialogType.normal,
                    }}
                >
                    <Checkbox
                        label="Archiv"
                        className={formStyles.checkbox}
                        checked={editData.archiv || false}
                        onChange={(event, checked) => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    archiv: checked || false,
                                });
                            }
                        }}
                    />
                    <FormTextField
                        value={editData.text}
                        savingState={SavingState.notChanged}
                        options={{ maxLength: 250 }}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    text: newValue as string,
                                });
                            }
                        }}
                        label="Text"
                    />
                    <FormTextField
                        value={editData.zahlfrist}
                        savingState={SavingState.notChanged}
                        options={{ numeric: true }}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    zahlfrist: newValue as number,
                                });
                            }
                        }}
                        label="Zahlfrist"
                    />
                    <FormTextField
                        value={editData.skP1}
                        savingState={SavingState.notChanged}
                        options={{ numeric: true }}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    skP1: newValue as number,
                                });
                            }
                        }}
                        label="Skonto - Prozent"
                    />
                    <FormTextField
                        value={editData.skF1}
                        savingState={SavingState.notChanged}
                        options={{ numeric: true }}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    skF1: newValue as number,
                                });
                            }
                        }}
                        label="Skonto - Frist"
                    />
                    <DialogFooter>
                        <DefaultButton onClick={() => setEditData(null)} text="Abbruch" />
                        <PrimaryButton onClick={save} text="Speichern" />
                    </DialogFooter>
                </Dialog>
            )}
        </Fabric>
    );
};
